import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Loader2, AlertTriangle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

// ─── Constants ────────────────────────────────────────────────────────────────

const ROUTE_OPTIONS = [
    { value: "administration_asset_purchase", label: "Administration — asset purchase" },
    { value: "solvent_share_purchase", label: "Solvent share purchase" },
    { value: "liquidation", label: "Liquidation" },
];

function emptyEmployee() {
    return {
        _key: Math.random().toString(36).slice(2),
        name: "",
        tenure_years: "",
        weekly_pay: "",
        age: "",
        has_db_pension: false,
        annual_holiday_days: "",
        holiday_days_taken_this_year: "",
        holiday_year_elapsed_pct: "",
    };
}

function fmtGBP(n) {
    if (n == null) return "—";
    return `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TupeCalculator() {
    const [transactionRoute, setTransactionRoute] = useState("administration_asset_purchase");
    const [employees, setEmployees] = useState([emptyEmployee()]);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [savedId, setSavedId] = useState(null);

    const updateEmployee = (key, field, value) => {
        setEmployees((rows) => rows.map((r) => (r._key === key ? { ...r, [field]: value } : r)));
    };

    const addEmployee = () => setEmployees((rows) => [...rows, emptyEmployee()]);

    const removeEmployee = (key) => setEmployees((rows) => (rows.length > 1 ? rows.filter((r) => r._key !== key) : rows));

    const canSubmit = employees.every((e) => e.tenure_years !== "" && e.weekly_pay !== "");

    const runCalculation = async () => {
        if (!canSubmit) {
            toast.error("Every employee needs at least tenure (years) and weekly pay.");
            return;
        }
        setRunning(true);
        setResult(null);
        setSavedId(null);
        try {
            const payload = {
                transaction_route: transactionRoute,
                employees: employees.map((e) => ({
                    name: e.name || null,
                    tenure_years: parseFloat(e.tenure_years) || 0,
                    weekly_pay: parseFloat(e.weekly_pay) || 0,
                    age: e.age === "" ? null : parseInt(e.age, 10),
                    has_db_pension: !!e.has_db_pension,
                    annual_holiday_days: e.annual_holiday_days === "" ? null : parseFloat(e.annual_holiday_days),
                    holiday_days_taken_this_year: e.holiday_days_taken_this_year === "" ? null : parseFloat(e.holiday_days_taken_this_year),
                    holiday_year_elapsed_pct: e.holiday_year_elapsed_pct === "" ? null : parseFloat(e.holiday_year_elapsed_pct),
                })),
            };
            const { data } = await api.post("/distressed/tupe-calculations", payload);
            setResult(data.result);
            setSavedId(data.id);
            toast.success("Calculation complete and saved.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC]">
            <PageHeader
                title="TUPE Calculator"
                subtitle="Estimated employment liability across redundancy, notice and holiday pay"
            />

            <div className="px-6 lg:px-8 py-6 max-w-5xl mx-auto space-y-6">
                <div
                    className="flex items-start gap-2.5 rounded-[6px] border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3"
                    data-testid="tupe-estimate-banner"
                >
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#B45309' }} />
                    <p className="text-[13px]" style={{ color: '#92400E' }}>
                        All figures below are <strong>estimates</strong> to support screening and negotiation
                        judgement — this is not a legal determination of actual liability. Confirm with
                        employment counsel before relying on these figures in a transaction.
                    </p>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-[15px]">Transaction route</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Select value={transactionRoute} onValueChange={setTransactionRoute}>
                            <SelectTrigger className="w-full sm:w-96" data-testid="tupe-route-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ROUTE_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-[12px] mt-2" style={{ color: '#64748B' }}>
                            The £ totals below don't change by route — but who's liable, and whether TUPE
                            applies at all, does. See the route-sensitivity note in the results.
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-[15px]">Employees</CardTitle>
                        <Button size="sm" variant="outline" onClick={addEmployee} data-testid="tupe-add-employee">
                            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add employee
                        </Button>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {employees.map((emp, idx) => (
                            <div
                                key={emp._key}
                                className="grid grid-cols-2 sm:grid-cols-6 gap-3 items-end p-3 rounded-[6px]"
                                style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}
                                data-testid={`tupe-employee-row-${idx}`}
                            >
                                <div className="col-span-2 sm:col-span-2">
                                    <Label className="text-[11px]">Name (optional)</Label>
                                    <Input
                                        value={emp.name}
                                        onChange={(e) => updateEmployee(emp._key, "name", e.target.value)}
                                        placeholder={`Employee ${idx + 1}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-[11px]">Tenure (yrs)</Label>
                                    <Input
                                        type="number" min="0" step="0.1"
                                        value={emp.tenure_years}
                                        onChange={(e) => updateEmployee(emp._key, "tenure_years", e.target.value)}
                                        data-testid={`tupe-tenure-${idx}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-[11px]">Weekly pay (£)</Label>
                                    <Input
                                        type="number" min="0" step="1"
                                        value={emp.weekly_pay}
                                        onChange={(e) => updateEmployee(emp._key, "weekly_pay", e.target.value)}
                                        data-testid={`tupe-weekly-pay-${idx}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-[11px]">Age</Label>
                                    <Input
                                        type="number" min="0" max="120"
                                        value={emp.age}
                                        onChange={(e) => updateEmployee(emp._key, "age", e.target.value)}
                                    />
                                </div>
                                <div className="flex items-center gap-2 pb-2">
                                    <Checkbox
                                        checked={emp.has_db_pension}
                                        onCheckedChange={(v) => updateEmployee(emp._key, "has_db_pension", !!v)}
                                        data-testid={`tupe-db-pension-${idx}`}
                                    />
                                    <Label className="text-[11px] cursor-pointer">DB pension</Label>
                                </div>
                                <div className="col-span-2 sm:col-span-6 flex justify-end">
                                    <Button
                                        size="sm" variant="ghost"
                                        onClick={() => removeEmployee(emp._key)}
                                        disabled={employees.length === 1}
                                    >
                                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>

                <div className="flex justify-end">
                    <Button onClick={runCalculation} disabled={running || !canSubmit} data-testid="tupe-run-calculation">
                        {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
                        Run calculation
                    </Button>
                </div>

                {result && (
                    <div className="space-y-4" data-testid="tupe-results-panel">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-[15px]">Aggregate totals (estimate)</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    <Stat label="Redundancy" value={fmtGBP(result.totals.redundancy)} />
                                    <Stat label="Notice" value={fmtGBP(result.totals.notice)} />
                                    <Stat label="Holiday" value={fmtGBP(result.totals.holiday)} />
                                    <Stat label="Total estimated liability" value={fmtGBP(result.totals.grand_total)} highlight />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-[15px]">Route-sensitivity note</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-[13px] leading-relaxed" style={{ color: '#334155' }}>
                                    {result.route_guidance}
                                </p>
                            </CardContent>
                        </Card>

                        {result.db_pension_flags?.length > 0 && (
                            <Card style={{ borderColor: '#FDE68A' }}>
                                <CardHeader>
                                    <CardTitle className="text-[15px]" style={{ color: '#92400E' }}>
                                        DB pension checklist ({result.db_pension_flags.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    {result.db_pension_flags.map((f, i) => (
                                        <p key={i} className="text-[13px]" style={{ color: '#92400E' }}>{f}</p>
                                    ))}
                                </CardContent>
                            </Card>
                        )}

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-[15px]">Per-employee breakdown</CardTitle>
                            </CardHeader>
                            <CardContent className="overflow-x-auto">
                                <table className="w-full text-[13px]" data-testid="tupe-breakdown-table">
                                    <thead>
                                        <tr className="text-left border-b" style={{ borderColor: '#E2E8F0', color: '#64748B' }}>
                                            <th className="py-2 pr-3 font-medium">Employee</th>
                                            <th className="py-2 pr-3 font-medium">Redundancy</th>
                                            <th className="py-2 pr-3 font-medium">Notice</th>
                                            <th className="py-2 pr-3 font-medium">Holiday</th>
                                            <th className="py-2 pr-3 font-medium">Total</th>
                                            <th className="py-2 pr-3 font-medium">Flags</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.employees.map((e, i) => (
                                            <tr key={i} className="border-b" style={{ borderColor: '#F1F5F9' }}>
                                                <td className="py-2 pr-3">{e.name || `Employee ${i + 1}`}</td>
                                                <td className="py-2 pr-3">
                                                    {fmtGBP(e.redundancy.amount)}
                                                    {e.redundancy.weekly_pay_was_capped && (
                                                        <Badge variant="outline" className="ml-1.5 text-[10px]">pay capped</Badge>
                                                    )}
                                                    {e.redundancy.eligible === false && (
                                                        <Badge variant="outline" className="ml-1.5 text-[10px]" title={e.redundancy.note}>
                                                            not eligible (&lt;2 yrs)
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td className="py-2 pr-3">{fmtGBP(e.notice.amount)}</td>
                                                <td className="py-2 pr-3">
                                                    {fmtGBP(e.holiday.amount)}
                                                    {e.holiday.prorated === false && e.holiday.note && (
                                                        <Badge variant="outline" className="ml-1.5 text-[10px]" title={e.holiday.note}>
                                                            not prorated
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td className="py-2 pr-3 font-semibold">{fmtGBP(e.total_estimated_liability)}</td>
                                                <td className="py-2 pr-3">
                                                    {e.db_pension_flag && (
                                                        <Badge variant="outline" style={{ color: '#B45309', borderColor: '#FDE68A' }}>
                                                            DB pension
                                                        </Badge>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardContent>
                        </Card>

                        {savedId && (
                            <p className="text-[12px]" style={{ color: '#64748B' }}>
                                Saved as calculation <code>{savedId}</code>.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wide" style={{ color: '#64748B' }}>{label}</div>
            <div
                className="text-[20px] font-semibold mt-0.5"
                style={{ color: highlight ? '#0B1F3A' : '#334155' }}
            >
                {value}
            </div>
        </div>
    );
}
